import { styled, GetProps, XStack, Text } from 'tamagui'
import type { ReactNode } from 'react'

const ButtonFrame = styled(XStack, {
  name: 'Button',
  tag: 'button',
  role: 'button',
  
  // Base styles
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$2',
  cursor: 'pointer',
  borderWidth: 2,
  borderRadius: 0, // Brutalist
  
  // Pressable states
  pressStyle: {
    opacity: 0.8,
    scale: 0.98,
  },
  
  disabledStyle: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  
  // Variants
  variants: {
    variant: {
      primary: {
        backgroundColor: '$color',
        borderColor: '$color',
        hoverStyle: {
          backgroundColor: '$colorMuted',
          borderColor: '$colorMuted',
        },
      },
      secondary: {
        backgroundColor: '$backgroundMuted',
        borderColor: '$borderColor',
        hoverStyle: {
          backgroundColor: '$borderColor',
        },
      },
      outline: {
        backgroundColor: 'transparent',
        borderColor: '$borderColor',
        hoverStyle: {
          backgroundColor: '$backgroundMuted',
        },
      },
      accent: {
        backgroundColor: '$accent',
        borderColor: '$accent',
        hoverStyle: {
          opacity: 0.9,
        },
      },
      ghost: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        hoverStyle: {
          backgroundColor: '$backgroundMuted',
        },
      },
    },
    size: {
      sm: {
        height: 36,
        paddingHorizontal: '$3',
      },
      md: {
        height: 44,
        paddingHorizontal: '$4',
      },
      lg: {
        height: 52,
        paddingHorizontal: '$6',
      },
    },
    width: {
      full: {
        width: '100%',
      },
      auto: {},
    },
  } as const,
  
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

const ButtonText = styled(Text, {
  name: 'ButtonText',
  fontFamily: '$body',
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: 1,
  
  variants: {
    variant: {
      primary: { color: '$background' },
      secondary: { color: '$color' },
      outline: { color: '$color' },
      accent: { color: '$accentForeground' },
      ghost: { color: '$color' },
    },
    size: {
      sm: { fontSize: 12 },
      md: { fontSize: 14 },
      lg: { fontSize: 16 },
    },
  } as const,
  
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

export interface ButtonProps extends GetProps<typeof ButtonFrame> {
  children: ReactNode
  icon?: ReactNode
  iconAfter?: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
}

export function Button({ 
  children, 
  icon,
  iconAfter,
  variant = 'primary', 
  size = 'md',
  onClick,
  type,
  ...props 
}: ButtonProps) {
  return (
    <ButtonFrame 
      variant={variant} 
      size={size} 
      onPress={onClick} 
      // @ts-expect-error - type is valid for web button element
      type={type}
      {...props}
    >
      {icon}
      <ButtonText variant={variant} size={size}>
        {children}
      </ButtonText>
      {iconAfter}
    </ButtonFrame>
  )
}

Button.displayName = 'Button'
